import { inject, Injectable, NgZone } from '@angular/core';
import {
  CurrentUserContext,
  DashboardProfileContextService,
  DashboardUserContextService,
  ProfileIdentifierWithVertical,
} from '@rma/authentication/public/user';
import { Environment } from '@rma/generic/util/environment';
import { combineLatest } from 'rxjs';
import { TrackJS } from 'trackjs';

@Injectable({
  providedIn: 'root',
})
export class ErrorTrackingService {
  private readonly profileContext = inject(DashboardProfileContextService);
  private readonly userContextService = inject(DashboardUserContextService);

  private readonly environment = inject(Environment);
  private readonly ngZone = inject(NgZone);

  public initialise() {
    const token = this.environment.trackjsId;
    if (token) {
      TrackJS.install({ token, application: 'dashboard' });
      this.ngZone.runOutsideAngular(() => {
        combineLatest([this.userContextService.userContext$, this.profileContext.profileIdentifierWithVertical$]).subscribe(
          ([user, profileIdentifiers]) => {
            this.identify(user, profileIdentifiers);
          },
        );
      });
    }
  }

  public identify(user: CurrentUserContext, profileIdentifiers: ProfileIdentifierWithVertical) {
    if (!TrackJS.isInstalled()) {
      this.initialise();
    }

    if (TrackJS.isInstalled()) {
      const { tenant, version } = this.environment;
      const { userId, email, isImpersonating } = user;

      this.configure(userId, version);
      this.addMetadata(tenant, email, isImpersonating, profileIdentifiers);
    }
  }

  private configure(userId: string, version: string) {
    const config = { userId, version };
    TrackJS.configure(config);
  }

  private addMetadata(tenant: string, email: string, isImpersonating: boolean, profileIdentifiers: ProfileIdentifierWithVertical) {
    const { profileCode, profileType, verticalType } = profileIdentifiers;
    const hqLink = this.hqLink(tenant, profileIdentifiers);
    const metaData = { tenant, email, isImpersonating, profileCode, profileType, verticalType, hqLink };
    try {
      Object.entries(metaData).forEach(([k, v]) => TrackJS.addMetadata(k, `${v}`));
    } catch (e) {
      console.error(e);
    }
  }

  private hqLink(tenant: string, profileIdentifiers: ProfileIdentifierWithVertical): string {
    const { profileType, profileCode } = profileIdentifiers;
    return new URL(`/${tenant}/crm/contacts/${profileType}/${profileCode}`, 'https://hq.ratemyagent.com').toString();
  }
}
